// CandidateList.jsx — candidates behind the funnel, modelled on the per-status
// lists on the right of the "Recruitment Dashboard _ New" sheet. The compact view
// shows who and where each candidate is; "detail" adds the manager / partner
// interviews, proposal, start date and reason. Columns sort on click.

import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { formatDate, statusInfo, statusNumber, todayIso } from '@/utils/recruitment/recruitment';
import { SERVICE_LINE_COLOURS } from '@/components/data/recruitment/StatusChart';

import { recruitmentCss as css } from '@digital_monorepo/styling';

// An interview/proposal cell shows the actual date, or the request date while it is still being booked.
const StageDate = ({ done, requested, doneLabel, requestedLabel }) => {
  if (done) return <span title={doneLabel}>{formatDate(done)}</span>;
  if (requested) return <span className={css.muted}>{requestedLabel} {formatDate(requested)}</span>;
  return <span className={css.muted}>—</span>;
};

StageDate.propTypes = {
  done: PropTypes.string,
  requested: PropTypes.string,
  doneLabel: PropTypes.string,
  requestedLabel: PropTypes.string
};

const FollowUp = ({ candidate, today }) => {
  const required = candidate.FollowUpRequired === 'Yes';
  const overdue = candidate.FollowUpDate && candidate.FollowUpDate < today;
  if (!candidate.FollowUpDate && !required) return <span className={css.muted}>—</span>;
  return (
    <span className={css.followUp}>
      {candidate.FollowUpDate && <span>{formatDate(candidate.FollowUpDate)}</span>}
      {required && <span className={css.tag}>Required</span>}
      {overdue && <span className={`${css.tag} ${css.tagWarning}`}>Overdue</span>}
    </span>
  );
};

FollowUp.propTypes = {
  candidate: PropTypes.object.isRequired,
  today: PropTypes.string.isRequired
};

// Blanks always sort last, whichever the direction.
const SORTS = {
  name: c => c.CandidateName || null,
  status: c => (statusNumber(c.Status) === '' ? null : Number(statusNumber(c.Status))),
  serviceLine: c => c.ServiceLine || null,
  level: c => c.Level || null,
  office: c => c.Office || null,
  experience: c => c.Experience || null,
  days: c => c.DaysOfProcess ?? null
};

const compare = (a, b) => (typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b)));

const SortHeader = ({ id, label, sort, onSort, numeric }) => {
  const active = sort.key === id;
  return (
    <th scope='col' className={numeric ? css.numeric : ''} aria-sort={active ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      <button type='button' className={`${css.sortButton} ${active ? css.sortActive : ''}`} onClick={() => onSort(id)}>
        {label}
        <span className={css.sortArrow} aria-hidden='true'>{active ? (sort.dir === 1 ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );
};

SortHeader.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  sort: PropTypes.object.isRequired,
  onSort: PropTypes.func.isRequired,
  numeric: PropTypes.bool
};

const CandidateList = ({ candidates, detail }) => {
  const today = todayIso();
  const [sort, setSort] = useState({ key: 'status', dir: 1 });

  const sorted = useMemo(() => {
    const value = SORTS[sort.key];
    return candidates.slice().sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (va === null || vb === null) return (va === null) - (vb === null);
      return sort.dir * compare(va, vb) || (a.CandidateName || '').localeCompare(b.CandidateName || '');
    });
  }, [candidates, sort]);

  const onSort = key => setSort(prev => ({ key, dir: prev.key === key ? -prev.dir : 1 }));

  if (!candidates.length) {
    return <p className={css.emptyNote}>No candidates match these filters.</p>;
  }

  const header = (id, label, numeric) => <SortHeader id={id} label={label} sort={sort} onSort={onSort} numeric={numeric} />;

  return (
    <div className={css.tableScroll}>
      <table className={css.candidateTable}>
        <thead>
          <tr>
            {header('name', 'Name')}
            {header('status', 'Status')}
            {header('serviceLine', 'Service line')}
            {header('level', 'Level')}
            {header('office', 'Office')}
            {header('experience', 'Experience')}
            {detail && (
              <>
                <th scope='col'>Manager</th>
                <th scope='col'>Manager interview</th>
                <th scope='col'>Partner</th>
                <th scope='col'>Partner interview</th>
                <th scope='col'>Proposal</th>
                <th scope='col'>Start date</th>
                <th scope='col'>Reason</th>
              </>
            )}
            <th scope='col'>Follow-up</th>
            {header('days', 'Days in process', true)}
          </tr>
        </thead>
        <tbody>
          {sorted.map((c, index) => (
            <tr key={`${c.CandidateName}-${index}`}>
              <th scope='row' className={css.candidateName}>{c.CandidateName}</th>
              <td>
                <span className={css.statusCell}>
                  <span className={css.statusNumber}>{statusNumber(c.Status)}</span>
                  {statusInfo(c.Status).label}
                </span>
              </td>
              <td>
                {c.ServiceLine ? (
                  <span className={css.serviceLineCell}>
                    <span className={css.legendSwatch} style={{ background: SERVICE_LINE_COLOURS[c.ServiceLine] || '#c9d3cd' }} aria-hidden='true' />
                    {c.ServiceLine}
                  </span>
                ) : <span className={css.muted}>—</span>}
              </td>
              <td>{c.Level || <span className={css.muted}>—</span>}</td>
              <td>{c.Office || <span className={css.muted}>—</span>}</td>
              <td>{c.Experience || <span className={css.muted}>—</span>}</td>
              {detail && (
                <>
                  <td>{c.Manager || <span className={css.muted}>—</span>}</td>
                  <td>
                    <StageDate done={c.ManagerInterviewDate} requested={c.ManagerInterviewRequestDate} doneLabel='Interview date' requestedLabel='Requested' />
                  </td>
                  <td>{c.Partner || <span className={css.muted}>—</span>}</td>
                  <td>
                    <StageDate done={c.PartnerInterviewDate} requested={c.PartnerInterviewRequestDate} doneLabel='Interview date' requestedLabel='Requested' />
                  </td>
                  <td>
                    <StageDate done={c.ProposalSubmissionDate} requested={c.ProposalRequestDate} doneLabel='Proposal submitted' requestedLabel='Requested' />
                  </td>
                  <td>{c.JobStartDate ? formatDate(c.JobStartDate) : <span className={css.muted}>—</span>}</td>
                  <td className={css.reasonCell}>{c.RejectionReason || <span className={css.muted}>—</span>}</td>
                </>
              )}
              <td><FollowUp candidate={c} today={today} /></td>
              <td className={css.numeric}>{c.DaysOfProcess ?? <span className={css.muted}>—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

CandidateList.propTypes = {
  candidates: PropTypes.arrayOf(PropTypes.object).isRequired,
  detail: PropTypes.bool
};

export default CandidateList;
